import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Stack,
  TableSortLabel,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Delete, TrendingUp, TrendingDown, Edit, Upload, Warning, CloudDownload, Balance, ShowChart, Close as CloseIcon, Archive as ArchiveIcon, Unarchive as UnarchiveIcon, Share as ShareIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { WatchlistEntry, Stock, UserProfile } from '../types';
import { StockService } from '../services/stockService';
import { StockPriceService } from '../services/stockPriceService';
import { WatchlistSnapshotService } from '../services/watchlistSnapshotService';
import { UserService } from '../services/userService';
import { SharedContentService } from '../services/sharedContentService';
import BulkImportDialog, { ImportedStock } from '../components/BulkImportDialog';
import EditableTableCell from '../components/EditableTableCell';
import TradeExecutionDialog from '../components/TradeExecutionDialog';
import EnhancedStockEditDialog from '../components/EnhancedStockEditDialog';
import AddStockDialog from '../components/AddStockDialog';
import { PortfolioService } from '../services/portfolioService';
import { PositionService } from '../services/positionService';
import { Portfolio } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase';
import { ApiUsageService } from '../services/apiUsageService';
import { differenceInDays } from 'date-fns';
import { safeFormatDate } from '../utils/dateUtils';
import { RecommendationService } from '../services/recommendationService';
import { StockChart } from '../components/StockChart';
import { useUser } from '../contexts/UserContext';
import { useStockHistory } from '../hooks/useStockHistory';
import HelpTooltip from '../components/HelpTooltip';
import { useMobileDetect } from '../hooks/useMobileDetect';
import { MobileWatchlistCard } from '../components/mobile';

type SortField = 'distanceFromPivot' | 'symbol' | 'addedAt' | 'volumeChgVs50Day' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';

const Watchlist: React.FC = () => {
  const { user, profile: userProfile } = useUser();
  const { isMobile } = useMobileDetect();
  const [watchlist, setWatchlist] = useState<(WatchlistEntry & { 
    stopLoss?: number; 
    targetPrice?: number; 
    lastUpdated?: Date; 
    priceWhenAdded?: number;
    epsRating?: number;
    rsRating?: number;
    profitStart?: number;
  })[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('distanceFromPivot');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; entry?: WatchlistEntry }>({ open: false });
  const [manualPrice, setManualPrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [apiUpdateInProgress, setApiUpdateInProgress] = useState(false);
  const [lastUpdateInfo, setLastUpdateInfo] = useState<{
    timestamp: Date | null;
    source: string | null;
    dataDelay?: number; // in minutes
  }>({ timestamp: null, source: null });
  const [tradeExecutionDialog, setTradeExecutionDialog] = useState<{
    open: boolean;
    entry: WatchlistEntry | null;
  }>({ open: false, entry: null });
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [tradeAnchorEl, setTradeAnchorEl] = useState<null | HTMLElement>(null);
  const [chartDialog, setChartDialog] = useState<{
    open: boolean;
    symbol: string | null;
    pivotPrice?: number;
    currentPrice: number;
    priceChangePercent: number;
    lastUpdated?: Date;
    currentVolume?: number;
    volumeChgVs50Day?: number;
    volumeSpikeRatio?: number;
    tradingDayProgress?: number;
    dataDelay?: number;
  }>({ open: false, symbol: null, currentPrice: 0, priceChangePercent: 0 });
  
  // New state for Active/Inactive tabs
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [inactivateDialog, setInactivateDialog] = useState<{
    open: boolean;
    entry: WatchlistEntry | null;
  }>({ open: false, entry: null });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    entry: WatchlistEntry | null;
  }>({ open: false, entry: null });
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasImportedPhilsWatchlist, setHasImportedPhilsWatchlist] = useState(false);
  const [importingPhilsWatchlist, setImportingPhilsWatchlist] = useState(false);
  const [hasPositionHistory, setHasPositionHistory] = useState<{[symbol: string]: boolean}>({});
  const [activePositionSymbols, setActivePositionSymbols] = useState<Set<string>>(new Set());
  const [isWatchlistShared, setIsWatchlistShared] = useState(false);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');

  useEffect(() => {
    const loadData = async () => {
      await loadWatchlist();
      await loadPortfolios();
      await loadUserData();
      // Load update info after watchlist is loaded
      await loadLastUpdateInfo();
    };
    loadData();
  }, []);

  // Reload user data when profile changes (e.g., after profile updates)
  useEffect(() => {
    if (user && userProfile) {
      loadUserData();
    }
  }, [userProfile]);

  // Load user-specific data (sharing status, import history)
  const loadUserData = async () => {
    if (user && userProfile) {
      console.log('Loading user data for:', user.uid);
      console.log('Profile from context:', userProfile);
      setIsAdmin(userProfile?.isAdmin || false);
      
      // Check if watchlist is shared
      const isShared = await SharedContentService.isContentShared(user.uid, 'watchlist');
      setIsWatchlistShared(isShared);
      
      // Check if user has imported Phil's watchlist
      const importHistory = userProfile?.watchlistImportHistory;
      if (importHistory?.lastImportedFrom === UserService.AdminUID) {
        setHasImportedPhilsWatchlist(true);
      }
    }
  };

  const loadPortfolios = async () => {
    try {
      const allPortfolios = await PortfolioService.getUserPortfolios();
      // Filter out inactive portfolios
      const activePortfolios = allPortfolios.filter(p => p.isActive !== false);

      // Track symbols with active positions across all portfolios
      const positionSymbols = new Set<string>();

      // Calculate available funds with pending for each portfolio
      const portfoliosWithCalculatedFunds = await Promise.all(
        activePortfolios.map(async (portfolio) => {
          // Get open and pending positions
          const [openPositions, pendingPositions] = await Promise.all([
            PositionService.getOpenPositions(portfolio.id),
            PositionService.getPendingPositions(portfolio.id)
          ]);

          // Collect symbols with active positions
          openPositions.forEach(p => positionSymbols.add(p.symbol));
          pendingPositions.forEach(p => positionSymbols.add(p.symbol));

          // Calculate open positions cost
          const openPositionsCost = openPositions.reduce(
            (sum, p) => sum + (p.entryPrice * p.quantity),
            0
          );

          // Calculate pending positions cost
          const pendingCost = pendingPositions.reduce(
            (sum, p) => sum + ((p.limitPrice || p.entryPrice) * p.quantity),
            0
          );

          // Calculate available funds accounting for realized losses
          const realizedLosses = Math.min(0, portfolio.totalEarnings || 0);
          const availableFunds = portfolio.initialFundValue - openPositionsCost + realizedLosses;
          const availableFundsWithPending = availableFunds - pendingCost;

          return {
            ...portfolio,
            availableFunds,
            availableFundsWithPending
          };
        })
      );

      setActivePositionSymbols(positionSymbols);
      setPortfolios(portfoliosWithCalculatedFunds);
      
      // Find the MarketSurge-2025-12 portfolio
      const marketSurgePortfolio = portfoliosWithCalculatedFunds.find(p => p.name === 'MarketSurge-2025-12');
      if (marketSurgePortfolio) {
        setSelectedPortfolio(marketSurgePortfolio);
      }
    } catch (error) {
      console.error('Error loading portfolios:', error);
    }
  };

  const loadWatchlist = async (includeInactive: boolean = false) => {
    try {
      const data = await StockService.getWatchlist(includeInactive);
      const sortedData = sortWatchlist(data);
      setWatchlist(sortedData);
      return sortedData; // Return the data so we can use it immediately
    } catch (error) {
      console.error('Error loading watchlist:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Reload watchlist when tab changes
  useEffect(() => {
    loadWatchlist(activeTab === 'inactive');
  }, [activeTab]);

  // Check position history for inactive stocks
  useEffect(() => {
    const checkPositionHistory = async () => {
      if (activeTab === 'inactive') {
        const user = auth.currentUser;
        if (!user) return;
        
        const historyMap: {[symbol: string]: boolean} = {};
        
        for (const entry of watchlist.filter(w => w.isActive === false)) {
          const hasHistory = await PositionService.hasPositionHistory(entry.symbol, user.uid);
          historyMap[entry.symbol] = hasHistory;
        }
        
        setHasPositionHistory(historyMap);
      }
    };
    
    checkPositionHistory();
  }, [watchlist, activeTab]);

  const loadLastUpdateInfo = async () => {
    try {
      console.log('Loading last update info...');
      
      // Get latest MarketSurge import
      const latestImport = await WatchlistSnapshotService.getLatestMarketSurgeImport();
      console.log('Latest MarketSurge import:', latestImport);
      
      // Get recent API updates
      const recentApiSnapshots = await WatchlistSnapshotService.getSnapshotsBySource('twelvedata', 1);
      const latestApiUpdate = recentApiSnapshots.length > 0 ? recentApiSnapshots[0] : null;
      console.log('Latest API update:', latestApiUpdate);
      
      // For now, also check watchlist entries directly for last update times
      if (!latestImport.timestamp && !latestApiUpdate && watchlist.length > 0) {
        // Find the most recent update from watchlist entries
        let mostRecentUpdate: Date | null = null;
        watchlist.forEach(entry => {
          if (entry.lastUpdated) {
            // Handle Firestore Timestamp objects
            const updateDate = entry.lastUpdated instanceof Date ? 
              entry.lastUpdated : 
              (entry.lastUpdated as any).toDate ? (entry.lastUpdated as any).toDate() : new Date(entry.lastUpdated);
            if (!isNaN(updateDate.getTime()) && (!mostRecentUpdate || updateDate > mostRecentUpdate)) {
              mostRecentUpdate = updateDate;
            }
          }
        });
        
        if (mostRecentUpdate) {
          console.log('Using watchlist lastUpdated:', mostRecentUpdate);
          setLastUpdateInfo({
            timestamp: mostRecentUpdate,
            source: 'Watchlist data'
          });
          return;
        }
      }
      
      // Determine which is more recent
      if (latestImport.timestamp && latestApiUpdate) {
        if (latestImport.timestamp > latestApiUpdate.timestamp) {
          setLastUpdateInfo({ 
            timestamp: latestImport.timestamp, 
            source: `MarketSurge (${latestImport.symbolCount} stocks)` 
          });
        } else {
          setLastUpdateInfo({ 
            timestamp: latestApiUpdate.timestamp, 
            source: 'Twelve Data API' 
          });
        }
      } else if (latestImport.timestamp) {
        setLastUpdateInfo({ 
          timestamp: latestImport.timestamp, 
          source: `MarketSurge (${latestImport.symbolCount} stocks)` 
        });
      } else if (latestApiUpdate) {
        setLastUpdateInfo({ 
          timestamp: latestApiUpdate.timestamp, 
          source: 'Twelve Data API' 
        });
      }
    } catch (error) {
      console.error('Error loading update info:', error);
      
      // Fallback: try to get last update from watchlist entries
      if (watchlist.length > 0) {
        let mostRecentUpdate: Date | null = null;
        watchlist.forEach(entry => {
          if (entry.lastUpdated) {
            // Handle Firestore Timestamp objects
            const updateDate = entry.lastUpdated instanceof Date ? 
              entry.lastUpdated : 
              (entry.lastUpdated as any).toDate ? (entry.lastUpdated as any).toDate() : new Date(entry.lastUpdated);
            if (!isNaN(updateDate.getTime()) && (!mostRecentUpdate || updateDate > mostRecentUpdate)) {
              mostRecentUpdate = updateDate;
            }
          }
        });
        
        if (mostRecentUpdate) {
          setLastUpdateInfo({
            timestamp: mostRecentUpdate,
            source: 'Watchlist data'
          });
        }
      }
    }
  };

  const sortWatchlist = (data: typeof watchlist) => {
    const sorted = [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'distanceFromPivot':
          aValue = a.distanceFromPivot ?? 999;
          bValue = b.distanceFromPivot ?? 999;
          break;
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'addedAt':
          aValue = new Date(a.addedAt).getTime();
          bValue = new Date(b.addedAt).getTime();
          break;
        case 'volumeChgVs50Day':
          aValue = a.volumeChgVs50Day ?? -999;
          bValue = b.volumeChgVs50Day ?? -999;
          break;
        case 'lastUpdated':
          aValue = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
          bValue = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    return sorted;
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    if (watchlist.length > 0) {
      const sortedData = sortWatchlist(watchlist);
      setWatchlist(sortedData);
    }
  }, [sortField, sortDirection]);
  
  // Reload update info when watchlist changes
  useEffect(() => {
    if (watchlist.length > 0) {
      loadLastUpdateInfo();
    }
  }, [watchlist.length]);


  const handleApiPriceUpdate = async () => {
    setApiUpdateInProgress(true);
    setError(null);
    
    try {
      const functions = getFunctions();
      // Try Alpaca first, then fallback to other providers
      let provider = 'Alpaca (15-min delayed)';
      
      // Always create the callable first, then handle errors during execution
      const alpacaPriceUpdate = httpsCallable(functions, 'manualPriceUpdateAlpaca');
      const finnhubPriceUpdate = httpsCallable(functions, 'manualPriceUpdateFinnhub');
      const twelveDataPriceUpdate = httpsCallable(functions, 'batchPriceUpdateTwelveData');
      
      const symbols = watchlist.map(entry => entry.symbol);
      
      // Try Alpaca first (best volume data)
      try {
        setError(`Updating ${symbols.length} stocks via Alpaca (15-min delayed)...`);
        
        const result = await alpacaPriceUpdate({ symbols });
        const updateResult = result.data as any;
        
        console.log('Alpaca update result:', updateResult);
        
        // Update last update info with delay indicator
        setLastUpdateInfo({ 
          timestamp: new Date(), 
          source: provider,
          dataDelay: 15 // Alpaca free tier has 15-minute delay
        });
        
        // Reload watchlist to show updates
        await loadWatchlist();
        
        // Position prices are updated by the backend during the API call
        // No need to update them from the frontend
        
        // Generate recommendations after update
        try {
          await RecommendationService.generateAndStoreRecommendations('alpaca');
          console.log('Recommendations generated after Alpaca update');
        } catch (recError) {
          console.error('Error generating recommendations:', recError);
        }
        
        setError(`Successfully updated ${symbols.length} prices from ${provider}`);
        setApiUpdateInProgress(false);
        return;
      } catch (error: any) {
        console.error('Error with Alpaca, falling back to Finnhub:', error);
        
        // Try Finnhub as first fallback
        try {
          provider = 'Finnhub';
          setError(`Updating ${symbols.length} stocks via Finnhub...`);
          
          const result = await finnhubPriceUpdate({ symbols });
          const updateResult = result.data as any;
          
          console.log('Finnhub update result:', updateResult);
          
          // Update last update info (no delay for Finnhub)
          setLastUpdateInfo({ 
            timestamp: new Date(), 
            source: provider,
            dataDelay: undefined
          });
          
          // Reload watchlist to show updates
          await loadWatchlist();
          
          // Position prices are updated by the backend during the API call
          // No need to update them from the frontend
          
          // Generate recommendations after update
          try {
            await RecommendationService.generateAndStoreRecommendations('finnhub');
            console.log('Recommendations generated after Finnhub update');
          } catch (recError) {
            console.error('Error generating recommendations:', recError);
          }
          
          setError(`Successfully updated ${symbols.length} prices from ${provider}`);
          setApiUpdateInProgress(false);
          return;
        } catch (finnhubError: any) {
          console.error('Error with Finnhub, falling back to TwelveData:', finnhubError);
          // Final fallback to TwelveData
          provider = 'Twelve Data API';
        }
      }
      
      // TwelveData batching logic
      const totalBatches = Math.ceil(symbols.length / 8);
      let totalUpdated = 0;
      let allPriceChanges: string[] = [];
      
      // Process batches one at a time
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        try {
          setError(`Updating batch ${batchIndex + 1} of ${totalBatches}...`);
          
          const result = await twelveDataPriceUpdate({ symbols, batchIndex });
          const batchResult = result.data as any;
          
          console.log(`Batch ${batchIndex + 1} result:`, batchResult);
          
          // Log API usage for this batch
          if (batchResult.updatedSymbols && batchResult.updatedSymbols.length > 0) {
            await ApiUsageService.logTwelveDataCall(batchResult.updatedSymbols);
          }
          
          // Reload watchlist after each batch to show updates
          const previousWatchlist = [...watchlist];
          await loadWatchlist();
          
          // Track price changes for this batch
          if (batchResult.updatedSymbols) {
            for (const symbol of batchResult.updatedSymbols) {
              const updated = watchlist.find(w => w.symbol === symbol);
              const previous = previousWatchlist.find(w => w.symbol === symbol);
              if (updated && previous && previous.currentPrice !== updated.currentPrice) {
                const change = ((updated.currentPrice - previous.currentPrice) / previous.currentPrice * 100).toFixed(2);
                allPriceChanges.push(`${symbol}: $${previous.currentPrice.toFixed(2)} → $${updated.currentPrice.toFixed(2)} (${change}%)`);
              }
            }
          }
          
          totalUpdated += batchResult.successCount || 0;
          
          // If there are more batches, wait 60 seconds for rate limit
          if (batchResult.hasMoreBatches) {
            setError(`Updated batch ${batchIndex + 1}/${totalBatches}. Waiting 60 seconds for rate limit...`);
            await new Promise(resolve => setTimeout(resolve, 60000));
          }
        } catch (batchError: any) {
          console.error(`Error in batch ${batchIndex + 1}:`, batchError);
          if (batchError.code === 'resource-exhausted') {
            setError(`Rate limit hit. Waiting 60 seconds before continuing...`);
            await new Promise(resolve => setTimeout(resolve, 60000));
            // Retry the same batch
            batchIndex--;
          } else {
            throw batchError;
          }
        }
      }
      
      // Update last update info (no delay for TwelveData)
      setLastUpdateInfo({ 
        timestamp: new Date(), 
        source: provider,
        dataDelay: undefined
      });
      
      // Final reload to ensure we have all updates
      await loadWatchlist();
      
      // Update position prices for all updated symbols
      try {
        // Position prices are updated by the backend during the API call
        // No need to update them from the frontend
      } catch (error) {
        console.error('TwelveData batch update error:', error);
      }
      
      // Generate recommendations after all updates
      try {
        await RecommendationService.generateAndStoreRecommendations('twelvedata');
        console.log('Recommendations generated after price update');
      } catch (recError) {
        console.error('Error generating recommendations:', recError);
      }
      
      if (allPriceChanges.length > 0) {
        console.log('All price changes:', allPriceChanges);
        setError(`Successfully updated ${totalUpdated} prices from Twelve Data`);
      } else {
        setError('Twelve Data update completed but no price changes detected');
      }
    } catch (error: any) {
      console.error('Error updating prices via API:', error);
      setError(error.message || 'Failed to update prices from API');
    } finally {
      setApiUpdateInProgress(false);
    }
  };

  const handleImportPhilsWatchlist = async () => {
    setImportingPhilsWatchlist(true);
    setError(null);
    
    try {
      const result = await StockService.importWatchlistFromUser(UserService.AdminUID, true);
      
      const message = `Successfully imported ${result.imported.length} new stocks` +
        (result.updated.length > 0 ? ` and updated ${result.updated.length} stocks` : '') +
        (result.skipped.length > 0 ? ` (${result.skipped.length} skipped)` : '');
      
      setError(message); // Use error state for success message temporarily
      setHasImportedPhilsWatchlist(true);
      
      // Reload watchlist
      await loadWatchlist(activeTab === 'inactive');
      
      // No need to run recommendations - as per requirements
    } catch (error: any) {
      console.error('Error importing Phil\'s watchlist:', error);
      setError(error.message || 'Failed to import watchlist');
    } finally {
      setImportingPhilsWatchlist(false);
    }
  };

  const handleShareToggle = async () => {
    const user = auth.currentUser;
    if (!user || !userProfile) {
      console.error('Cannot share: user or profile not loaded');
      setError('Profile not loaded. Please refresh the page and try again.');
      return;
    }
    
    setSharingLoading(true);
    setError(null);
    
    try {
      if (isWatchlistShared) {
        // Unshare the watchlist
        await SharedContentService.unshareWatchlist(user.uid);
        setIsWatchlistShared(false);
      } else {
        // Share the watchlist
        console.log('Sharing watchlist with profile:', userProfile);
        console.log('Profile fields:', {
          displayName: userProfile.displayName,
          email: userProfile.email,
          handle: userProfile.handle,
          profilePicUrl: userProfile.profilePicUrl
        });
        
        // Create a clean profile object to avoid undefined fields
        const cleanProfile: UserProfile = {
          uid: userProfile.uid,
          email: userProfile.email,
          isAdmin: userProfile.isAdmin,
          createdAt: userProfile.createdAt,
          updatedAt: userProfile.updatedAt,
        };
        
        // Only add optional fields if they exist and are not undefined
        if (userProfile.displayName !== undefined) cleanProfile.displayName = userProfile.displayName;
        if (userProfile.handle !== undefined) cleanProfile.handle = userProfile.handle;
        if (userProfile.bio !== undefined) cleanProfile.bio = userProfile.bio;
        if (userProfile.profilePicUrl !== undefined) cleanProfile.profilePicUrl = userProfile.profilePicUrl;
        if (userProfile.sharingPreferences !== undefined) cleanProfile.sharingPreferences = userProfile.sharingPreferences;
        
        console.log('Clean profile for sharing:', cleanProfile);
        
        await SharedContentService.shareWatchlist(user.uid, cleanProfile);
        setIsWatchlistShared(true);
      }
    } catch (error: any) {
      console.error('Error toggling watchlist sharing:', error);
      setError(error.message || 'Failed to update sharing status');
    } finally {
      setSharingLoading(false);
    }
  };


  const handleAddStock = async (stockData: any) => {
    setError(null);
    try {
      const symbol = stockData.symbol.toUpperCase();
      
      // Check if stock already exists in watchlist
      const existingStock = watchlist.find(w => w.symbol === symbol);
      if (existingStock) {
        throw new Error('Stock already exists in watchlist');
      }
      
      console.log('Adding stock with data:', stockData);
      
      const priceWhenAdded = stockData.currentPrice;
      const stock: Omit<Stock, 'lastUpdated' | 'priceHistory' | 'priceWhenAdded'> & { priceWhenAdded?: number } = {
        symbol,
        companyName: stockData.companyName,
        currentPrice: priceWhenAdded, // This will be updated by API calls
        priceWhenAdded: priceWhenAdded,
        addedToWatchlist: new Date(),
      };

      // Add optional fields if provided
      if (stockData.pivotPrice) stock.pivotPrice = stockData.pivotPrice;
      if (stockData.stopLoss) stock.stopLoss = stockData.stopLoss;
      if (stockData.targetPrice) stock.targetPrice = stockData.targetPrice;
      if (stockData.marketSurgeScore) stock.marketSurgeScore = stockData.marketSurgeScore;
      
      // Add technical fields if provided
      if (stockData.weeklyChgToPivot !== undefined) (stock as any).weeklyChgToPivot = stockData.weeklyChgToPivot;
      if (stockData.weeklyBaseType) (stock as any).weeklyBaseType = stockData.weeklyBaseType;
      if (stockData.weeklyBaseStage) (stock as any).weeklyBaseStage = stockData.weeklyBaseStage;
      if (stockData.avgDailyVolume) (stock as any).avgDailyVolume = stockData.avgDailyVolume;
      if (stockData.volumeChgVs50Day !== undefined) (stock as any).volumeChgVs50Day = stockData.volumeChgVs50Day;
      if (stockData.industryName) (stock as any).industryName = stockData.industryName;
      if (stockData.epsRating) stock.epsRating = stockData.epsRating;
      if (stockData.rsRating) stock.rsRating = stockData.rsRating;
      if (stockData.compRating !== undefined) (stock as any).compRating = stockData.compRating;
      if (stockData.marketCap !== undefined) (stock as any).marketCap = stockData.marketCap;
      if (stockData.volume !== undefined) (stock as any).volume = stockData.volume;

      console.log('Formatted stock data:', stock);
      await StockService.addToWatchlist(stock);
      console.log('Stock added successfully');
      
      // Close dialog
      setDialogOpen(false);
      
      // Show success message
      if (stockData.pivotPrice) {
        setSnackbarMessage('Stock added successfully! Visit the Dashboard to see recommendations.');
      } else {
        setSnackbarMessage('Stock added successfully! Add a pivot price to enable recommendations.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Reload watchlist
      await loadWatchlist();
    } catch (error: any) {
      console.error('Error adding stock:', error);
      throw error; // Re-throw to let dialog handle it
    }
  };

  const handleBulkImport = async (importedStocks: ImportedStock[]) => {
    setImportProgress({ current: 0, total: importedStocks.length });
    
    let addedCount = 0;
    let updatedCount = 0;
    const snapshots: any[] = [];
    const symbolPrices: { symbol: string; currentPrice: number }[] = [];
    const importTimestamp = Date.now();
    
    // Extract the import filename if available from any stock
    let importFileName: string | undefined;
    
    console.log('Importing stocks:', importedStocks);
    
    for (let i = 0; i < importedStocks.length; i++) {
      const stock = importedStocks[i];
      console.log(`Processing ${stock.symbol}, isUpdate: ${stock.isUpdate}`);
      try {
        // Use provided current price or fetch from API
        let currentPrice = stock.currentPrice;
        if (!currentPrice) {
          try {
            currentPrice = await StockPriceService.getCurrentPrice(stock.symbol);
          } catch (error) {
            console.warn(`Could not fetch price for ${stock.symbol}, using provided price`);
            currentPrice = stock.pivotPrice || 0;
          }
        }

        const stockData: any = {
          symbol: stock.symbol,
          companyName: stock.companyName || stock.symbol,
          currentPrice,
        };
        
        // Only set addedToWatchlist for new stocks
        if (!stock.isUpdate) {
          stockData.addedToWatchlist = new Date();
        }
        
        // Calculate pivot price if we have weeklyChgToPivot data
        let calculatedPivotPrice: number | undefined;
        if (stock.weeklyChgToPivot !== undefined && currentPrice) {
          calculatedPivotPrice = StockService.calculatePivotPrice(currentPrice, stock.weeklyChgToPivot);
          stockData.pivotPrice = calculatedPivotPrice;
        } else if (stock.pivotPrice !== undefined) {
          stockData.pivotPrice = stock.pivotPrice;
          calculatedPivotPrice = stock.pivotPrice;
        }
        
        // Calculate target price if we have pivot
        if (calculatedPivotPrice) {
          stockData.targetPrice = StockService.calculateTargetPrice(calculatedPivotPrice);
        } else if (stock.targetPrice !== undefined) {
          stockData.targetPrice = stock.targetPrice;
        }
        
        if (stock.stopLoss !== undefined) stockData.stopLoss = stock.stopLoss;
        if (stock.epsRating !== undefined) stockData.epsRating = stock.epsRating;
        if (stock.rsRating !== undefined) stockData.rsRating = stock.rsRating;
        if (stock.weeklyChgToPivot !== undefined) stockData.weeklyChgToPivot = stock.weeklyChgToPivot;
        if (stock.weeklyBaseType !== undefined) stockData.weeklyBaseType = stock.weeklyBaseType;
        if (stock.weeklyBaseStage !== undefined) stockData.weeklyBaseStage = stock.weeklyBaseStage;
        if (stock.priceChgPercent !== undefined) stockData.priceChgPercent = stock.priceChgPercent;
        if (stock.percentOffHigh !== undefined) stockData.percentOffHigh = stock.percentOffHigh;
        if (stock.volumeChgVs50Day !== undefined) stockData.volumeChgVs50Day = stock.volumeChgVs50Day;
        if (stock.avgDailyVolume !== undefined) stockData.avgDailyVolume = stock.avgDailyVolume;
        if (stock.marketCap !== undefined) stockData.marketCap = stock.marketCap;
        if (stock.compRating !== undefined) stockData.compRating = stock.compRating;
        if (stock.adRating !== undefined) stockData.adRating = stock.adRating;
        if (stock.smrRating !== undefined) stockData.smrRating = stock.smrRating;
        if (stock.indGroupRank !== undefined) stockData.indGroupRank = stock.indGroupRank;
        if (stock.industryName !== undefined) stockData.industryName = stock.industryName;

        // Only set priceWhenAdded for new stocks, not updates
        if (!stock.isUpdate) {
          stockData.priceWhenAdded = currentPrice;
        }

        if (stock.isUpdate) {
          await StockService.updateWatchlistStock(stockData);
          updatedCount++;
        } else {
          await StockService.addToWatchlist(stockData);
          addedCount++;
        }
        
        // Create snapshot for historical tracking (filter out undefined values)
        const snapshot: any = {
          symbol: stock.symbol,
          source: 'marketsurge',
          currentPrice: currentPrice || 0,
          importTimestamp: Math.floor(importTimestamp / 1000),
        };
        
        // Add import file name if available (determined at import time)
        if (importFileName) {
          snapshot.importFileName = importFileName;
        }
        
        // Only add defined values  
        // Use the calculated pivot price if available
        if (calculatedPivotPrice !== undefined) {
          snapshot.pivotPrice = calculatedPivotPrice;
        } else if (stock.pivotPrice !== undefined) {
          snapshot.pivotPrice = stock.pivotPrice;
        }
        if (stock.weeklyChgToPivot !== undefined) snapshot.weeklyChgToPivot = stock.weeklyChgToPivot;
        if (stock.weeklyBaseType !== undefined) snapshot.weeklyBaseType = stock.weeklyBaseType;
        if (stock.weeklyBaseStage !== undefined) snapshot.weeklyBaseStage = stock.weeklyBaseStage;
        if (stock.priceChgPercent !== undefined) snapshot.priceChgPercent = stock.priceChgPercent;
        if (stock.percentOffHigh !== undefined) snapshot.percentOffHigh = stock.percentOffHigh;
        if (stock.volume !== undefined) snapshot.volume = stock.volume;
        if (stock.volumeChgVs50Day !== undefined) snapshot.volumeChgVs50Day = stock.volumeChgVs50Day;
        if (stock.avgDailyVolume !== undefined) snapshot.avgDailyVolume = stock.avgDailyVolume;
        if (stock.marketCap !== undefined) snapshot.marketCap = stock.marketCap;
        if (stock.compRating !== undefined) snapshot.compRating = stock.compRating;
        if (stock.epsRating !== undefined) snapshot.epsRating = stock.epsRating;
        if (stock.rsRating !== undefined) snapshot.rsRating = stock.rsRating;
        if (stock.adRating !== undefined) snapshot.adRating = stock.adRating;
        if (stock.smrRating !== undefined) snapshot.smrRating = stock.smrRating;
        if (stock.indGroupRank !== undefined) snapshot.indGroupRank = stock.indGroupRank;
        if (stock.industryName !== undefined) snapshot.industryName = stock.industryName;
        
        snapshots.push(snapshot);
        
        // Track the current price for position updates
        if (currentPrice) {
          symbolPrices.push({
            symbol: stock.symbol,
            currentPrice: currentPrice
          });
        }
        
        setImportProgress({ current: i + 1, total: importedStocks.length });
      } catch (error) {
        console.error(`Error importing ${stock.symbol}:`, error);
      }
    }
    
    // Save all snapshots
    if (snapshots.length > 0) {
      try {
        await WatchlistSnapshotService.saveMultipleSnapshots(snapshots);
        console.log(`Saved ${snapshots.length} historical snapshots`);
      } catch (error) {
        console.error('Error saving snapshots:', error);
      }
    }
    
    setImportProgress({ current: 0, total: 0 });
    
    // Show success message
    if (addedCount > 0 || updatedCount > 0) {
      const message = `Successfully imported: ${addedCount} new stocks${updatedCount > 0 ? `, ${updatedCount} updated` : ''}`;
      console.log(message);
      setError(message); // Use error state for success message temporarily
      
      // Update last update info
      setLastUpdateInfo({ 
        timestamp: new Date(), 
        source: `MarketSurge (${importedStocks.length} stocks)` 
      });
      
      // Update position prices for imported symbols
      try {
        if (symbolPrices.length > 0) {
          // Update position prices for any matching symbols
          await PositionService.updatePositionPricesBySymbol(symbolPrices);
          console.log(`Updated portfolio position prices for ${symbolPrices.length} symbols after MarketSurge import`);
        }
      } catch (positionError) {
        console.error('Error updating position prices:', positionError);
        // Don't fail the entire operation if position update fails
      }
      
      // Generate recommendations after import
      try {
        await RecommendationService.generateAndStoreRecommendations('marketsurge');
        console.log('Recommendations generated after MarketSurge import');
      } catch (recError) {
        console.error('Error generating recommendations:', recError);
        // Don't fail the operation if recommendation generation fails
      }
    }
    
    await loadWatchlist();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const handleUpdateField = async (symbol: string, field: string, value: number | undefined) => {
    try {
      await StockService.updateStockField(symbol, field, value);
      await loadWatchlist();
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    }
  };

  const getDistanceColor = (distance: number) => {
    const absDistance = Math.abs(distance);
    if (absDistance <= 2) return 'success';
    if (absDistance <= 5) return 'warning';
    return 'default';
  };

  // Mobile view rendering
  const renderMobileView = () => {
    const filteredWatchlist = watchlist.filter(entry =>
      activeTab === 'active' ? entry.isActive !== false : entry.isActive === false
    );

    return (
      <Box sx={{ p: 1.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Watchlist</Typography>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => setBulkImportOpen(true)} color="primary">
              <Upload />
            </IconButton>
            <IconButton onClick={() => setDialogOpen(true)} color="primary">
              <Add />
            </IconButton>
          </Stack>
        </Box>

        {/* Last Update Info (compact) */}
        {lastUpdateInfo.timestamp && (
          <Box sx={{ mb: 1.5, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Updated: {safeFormatDate(lastUpdateInfo.timestamp, 'MMM d, h:mm a')}
              {lastUpdateInfo.dataDelay && ` (${lastUpdateInfo.dataDelay}m delay)`}
            </Typography>
          </Box>
        )}

        {/* Active/Inactive Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab label={`Active (${watchlist.filter(w => w.isActive !== false).length})`} value="active" />
            <Tab label={`Inactive (${watchlist.filter(w => w.isActive === false).length})`} value="inactive" />
          </Tabs>
        </Box>

        {/* Stock Cards */}
        <Box sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {filteredWatchlist.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                {activeTab === 'active' ? 'No active stocks' : 'No inactive stocks'}
              </Typography>
            </Box>
          ) : (
            filteredWatchlist.map((entry, index) => (
              <MobileWatchlistCard
                key={`${entry.symbol}_${index}`}
                entry={{
                  ...entry,
                  companyName: (entry as any).companyName || entry.symbol,
                }}
                hasActivePosition={activePositionSymbols.has(entry.symbol)}
                onViewChart={(e) => {
                  setChartDialog({
                    open: true,
                    symbol: e.symbol,
                    pivotPrice: e.pivotPrice,
                    currentPrice: e.currentPrice,
                    priceChangePercent: e.priceChgPercent || 0,
                    lastUpdated: entry.lastUpdated,
                    currentVolume: entry.volume,
                    volumeChgVs50Day: entry.volumeChgVs50Day,
                    volumeSpikeRatio: entry.volumeSpikeRatio,
                    tradingDayProgress: entry.tradingDayProgress,
                    dataDelay: entry.dataDelay
                  });
                }}
                onTrade={(e, event) => {
                  setTradeAnchorEl(event.currentTarget);
                  setTradeExecutionDialog({ open: false, entry: e });
                }}
                onEdit={(e) => {
                  setEditDialog({ open: true, entry: e });
                  setManualPrice(e.currentPrice.toString());
                }}
              />
            ))
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  // Render mobile view for small screens
  if (isMobile) {
    return (
      <>
        {renderMobileView()}

        {/* Dialogs - shared between mobile and desktop */}
        <AddStockDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onAdd={handleAddStock}
        />
        <EnhancedStockEditDialog
          open={editDialog.open}
          onClose={() => setEditDialog({ open: false })}
          stock={editDialog.entry || null}
          onUpdate={async () => {
            await loadWatchlist();
            setSnackbarMessage('Stock updated successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }}
        />
        <BulkImportDialog
          open={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          onImport={handleBulkImport}
        />
        <Menu
          anchorEl={tradeAnchorEl}
          open={Boolean(tradeAnchorEl)}
          onClose={() => setTradeAnchorEl(null)}
        >
          {portfolios.map((portfolio) => (
            <MenuItem
              key={portfolio.id}
              onClick={() => {
                setSelectedPortfolio(portfolio);
                setTradeExecutionDialog({
                  open: true,
                  entry: tradeExecutionDialog.entry
                });
                setTradeAnchorEl(null);
              }}
            >
              {portfolio.name} ({formatCurrency((portfolio as any).availableFundsWithPending || portfolio.availableFunds)} avail)
            </MenuItem>
          ))}
        </Menu>
        {selectedPortfolio && (
          <TradeExecutionDialog
            open={tradeExecutionDialog.open}
            onClose={() => setTradeExecutionDialog({ open: false, entry: null })}
            watchlistEntry={tradeExecutionDialog.entry}
            portfolioId={selectedPortfolio.id}
            availableFunds={(selectedPortfolio as any).availableFundsWithPending || selectedPortfolio.availableFunds}
            onTradeExecuted={() => {
              loadWatchlist();
              loadPortfolios();
            }}
          />
        )}
        <StockChartDialog
          chartDialog={chartDialog}
          onClose={() => setChartDialog({ ...chartDialog, open: false })}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Watchlist
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Show Import Phil's Watchlist button for non-admin users */}
          {!isAdmin && (
            <Button
              variant="outlined"
              startIcon={<CloudDownload />}
              onClick={handleImportPhilsWatchlist}
              disabled={importingPhilsWatchlist}
              color="secondary"
            >
              {importingPhilsWatchlist ? 'Importing...' : 
               hasImportedPhilsWatchlist ? 'Update from Phil\'s Watchlist' : 
               'Import Phil\'s Watchlist'}
            </Button>
          )}
          {/* Show Live Prices for admin users */}
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<CloudDownload />}
              onClick={handleApiPriceUpdate}
              disabled={apiUpdateInProgress || watchlist.length === 0}
              color="primary"
            >
              {apiUpdateInProgress ? 'Fetching...' : 'Live Prices'}
            </Button>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setBulkImportOpen(true)}
            >
              Bulk Import
            </Button>
            <HelpTooltip 
              title="Import multiple stocks at once from MarketSurge CSV exports. Go to MarketSurge > Lists > Export to get your watchlist data, then import it here to track pivot points and technical indicators." 
            />
          </Box>
          <Button
            variant={isWatchlistShared ? "contained" : "outlined"}
            startIcon={<ShareIcon />}
            onClick={handleShareToggle}
            disabled={sharingLoading}
            color={isWatchlistShared ? "success" : "primary"}
          >
            {sharingLoading ? 'Updating...' : isWatchlistShared ? 'Shared' : 'Share'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Add Stock
          </Button>
        </Box>
      </Box>

      {lastUpdateInfo.timestamp && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="textSecondary">
            Last update: {lastUpdateInfo.timestamp ? 
              safeFormatDate(lastUpdateInfo.timestamp, 'MMM d, yyyy h:mm a') + ' EST' : 
              'No updates yet'}
            {lastUpdateInfo.source && ` from ${lastUpdateInfo.source}`}
          </Typography>
          {lastUpdateInfo.dataDelay && (
            <Chip
              label={`${lastUpdateInfo.dataDelay}-min delayed`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 2 }}
            />
          )}
        </Box>
      )}

      {/* Active/Inactive Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="watchlist tabs"
        >
          <Tab label="Active" value="active" />
          <Tab label="Inactive" value="inactive" />
        </Tabs>
      </Box>

      {activeTab === 'inactive' && watchlist.filter(w => w.isActive === false).length > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.main">
            Inactive stocks will not be considered in future Recommendations, but can still exist as positions of any status in Portfolios.
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'symbol'}
                  direction={sortField === 'symbol' ? sortDirection : 'asc'}
                  onClick={() => handleSort('symbol')}
                >
                  Symbol
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Current</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  % Today
                  <HelpTooltip
                    title="Today's price change percentage. This matches the daily change shown in stock charts."
                  />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Since Added
                  <HelpTooltip
                    title="Percentage change since the stock was added to your watchlist. This reflects your actual gain/loss from when you started tracking this stock."
                  />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Pivot Price
                  <HelpTooltip 
                    title="The pivot price is a key technical level where the stock has previously found support or resistance. Breaking above the pivot with volume often signals the start of a new upward move." 
                  />
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'distanceFromPivot'}
                    direction={sortField === 'distanceFromPivot' ? sortDirection : 'asc'}
                    onClick={() => handleSort('distanceFromPivot')}
                  >
                    Distance
                  </TableSortLabel>
                  <HelpTooltip 
                    title="The percentage distance from the current price to the pivot price. Positive values mean the stock is above the pivot, negative values mean below. Stocks within ±2% of pivot are often good entry candidates." 
                  />
                </Box>
              </TableCell>
              <TableCell align="right">Target Price</TableCell>
              <TableCell align="center">Base</TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'volumeChgVs50Day'}
                    direction={sortField === 'volumeChgVs50Day' ? sortDirection : 'desc'}
                    onClick={() => handleSort('volumeChgVs50Day')}
                  >
                    Vol %
                  </TableSortLabel>
                  <HelpTooltip 
                    title="The percentage change in today's volume compared to the 50-day average volume. High volume (>50%) on price breakouts confirms strong institutional buying." 
                  />
                </Box>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'addedAt'}
                  direction={sortField === 'addedAt' ? sortDirection : 'desc'}
                  onClick={() => handleSort('addedAt')}
                >
                  Date Added
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'lastUpdated'}
                  direction={sortField === 'lastUpdated' ? sortDirection : 'desc'}
                  onClick={() => handleSort('lastUpdated')}
                >
                  Last Updated
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {watchlist
              .filter(entry => activeTab === 'active' ? entry.isActive !== false : entry.isActive === false)
              .map((entry, index) => {
              const stock = watchlist.find(w => w.symbol === entry.symbol);
              const companyName = stock ? (stock as any).companyName : entry.symbol;
              
              return (
                <TableRow key={`${entry.symbol}_${index}`}>
                  <TableCell>
                    <Box
                      onClick={() => {
                        setChartDialog({
                          open: true,
                          symbol: entry.symbol,
                          pivotPrice: entry.pivotPrice,
                          currentPrice: entry.currentPrice,
                          priceChangePercent: entry.priceChgPercent || 0,
                          lastUpdated: entry.lastUpdated,
                          currentVolume: entry.volume,
                          volumeChgVs50Day: entry.volumeChgVs50Day,
                          volumeSpikeRatio: entry.volumeSpikeRatio,
                          tradingDayProgress: entry.tradingDayProgress,
                          dataDelay: entry.dataDelay
                        });
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {entry.symbol}
                        </Typography>
                        {activePositionSymbols.has(entry.symbol) && (
                          <Tooltip title="You own this stock" arrow>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 150 }}>
                        {companyName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(entry.currentPrice)}
                    {entry.priceWhenAdded && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        Added: {formatCurrency(entry.priceWhenAdded)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {entry.priceChgPercent !== undefined ? (
                      <Chip
                        label={`${entry.priceChgPercent >= 0 ? '+' : ''}${entry.priceChgPercent.toFixed(2)}%`}
                        color={entry.priceChgPercent >= 0 ? 'success' : 'error'}
                        size="small"
                        icon={entry.priceChgPercent >= 0 ? <TrendingUp /> : <TrendingDown />}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {entry.priceWhenAdded ? (
                      <Stack spacing={0.5}>
                        <Typography
                          variant="body2"
                          color={entry.currentPrice >= entry.priceWhenAdded ? 'success.main' : 'error.main'}
                        >
                          {entry.currentPrice >= entry.priceWhenAdded ? '+' : ''}
                          {formatCurrency(entry.currentPrice - entry.priceWhenAdded)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={entry.currentPrice >= entry.priceWhenAdded ? 'success.main' : 'error.main'}
                        >
                          {entry.currentPrice >= entry.priceWhenAdded ? '+' : ''}
                          {(((entry.currentPrice - entry.priceWhenAdded) / entry.priceWhenAdded) * 100).toFixed(2)}%
                        </Typography>
                      </Stack>
                    ) : '-'}
                  </TableCell>
                  <EditableTableCell
                    value={entry.pivotPrice}
                    onSave={(value) => handleUpdateField(entry.symbol, 'pivotPrice', value)}
                    placeholder="Set pivot"
                    isAutoCalculated={entry.weeklyChgToPivot !== undefined}
                  />
                  <TableCell align="center">
                    {entry.pivotPrice && entry.distanceFromPivot !== null ? (
                      <Chip
                        label={formatPercent(entry.distanceFromPivot)}
                        color={getDistanceColor(entry.distanceFromPivot)}
                        size="small"
                        icon={entry.distanceFromPivot >= 0 ? <TrendingUp /> : <TrendingDown />}
                      />
                    ) : (
                      <Chip
                        label="No pivot"
                        size="small"
                        icon={<Warning />}
                        color="warning"
                      />
                    )}
                  </TableCell>
                  <EditableTableCell
                    value={entry.targetPrice}
                    onSave={(value) => handleUpdateField(entry.symbol, 'targetPrice', value)}
                    placeholder="Set target"
                    isAutoCalculated={entry.weeklyChgToPivot !== undefined && entry.pivotPrice !== undefined}
                  />
                  <TableCell align="center">
                    {entry.weeklyBaseType ? (
                      <Box>
                        <Typography variant="caption" fontWeight="medium">
                          {entry.weeklyBaseType}
                        </Typography>
                        {entry.weeklyBaseStage && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            Stage {entry.weeklyBaseStage}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {entry.volumeChgVs50Day !== undefined ? (
                      <Chip
                        label={`${entry.volumeChgVs50Day > 0 ? '+' : ''}${entry.volumeChgVs50Day.toFixed(0)}%`}
                        size="small"
                        color={entry.volumeChgVs50Day > 50 ? 'success' : entry.volumeChgVs50Day > 0 ? 'default' : 'error'}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {entry.addedAt ? (
                      <Box>
                        <Typography variant="body2">
                          {safeFormatDate(entry.addedAt, 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {differenceInDays(new Date(), new Date(entry.addedAt))} days
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {entry.lastUpdated ? (
                      <Box>
                        <Typography variant="body2">
                          {safeFormatDate(entry.lastUpdated, 'h:mm a')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {safeFormatDate(entry.lastUpdated, 'MMM d')}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => {
                          setChartDialog({
                            open: true,
                            symbol: entry.symbol,
                            pivotPrice: entry.pivotPrice,
                            currentPrice: entry.currentPrice,
                            priceChangePercent: entry.priceChgPercent || 0,
                            lastUpdated: entry.lastUpdated,
                            currentVolume: entry.volume,
                            volumeChgVs50Day: entry.volumeChgVs50Day,
                            volumeSpikeRatio: entry.volumeSpikeRatio,
                            tradingDayProgress: entry.tradingDayProgress
                          });
                        }}
                        title="View chart"
                      >
                        <ShowChart />
                      </IconButton>
                      <Tooltip title="View on Finviz">
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://finviz.com/quote.ashx?t=${entry.symbol}&p=d`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ p: 0.5 }}
                        >
                          <img
                            src="https://finviz.com/favicon.ico"
                            alt="Finviz"
                            style={{ width: 18, height: 18 }}
                          />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={(e) => {
                          setTradeAnchorEl(e.currentTarget);
                          setTradeExecutionDialog({ open: false, entry });
                        }}
                        title="Execute trade"
                      >
                        <Balance />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setEditDialog({ 
                            open: true, 
                            entry: entry 
                          });
                          setManualPrice(entry.currentPrice.toString());
                        }}
                        title="View/Edit stock details"
                      >
                        <Edit />
                      </IconButton>
                      {activeTab === 'active' ? (
                        <Tooltip title="Make inactive">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => setInactivateDialog({ open: true, entry })}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Reactivate">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={async () => {
                                try {
                                  await StockService.reactivateStock(entry.symbol);
                                  await loadWatchlist(true);
                                } catch (error) {
                                  console.error('Error reactivating stock:', error);
                                  setError('Failed to reactivate stock');
                                }
                              }}
                            >
                              <UnarchiveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip 
                            title={hasPositionHistory[entry.symbol] ? 
                              "Cannot delete - has position history" : 
                              "Permanently delete"}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={hasPositionHistory[entry.symbol]}
                                onClick={() => setDeleteConfirmDialog({ open: true, entry })}
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {watchlist.filter(w => activeTab === 'active' ? w.isActive !== false : w.isActive === false).length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            {activeTab === 'active' ? 'No active stocks in watchlist' : 'No inactive stocks'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {activeTab === 'active' ? 'Add stocks from MarketSurge to start tracking pivot points' : 
             'Inactive stocks will appear here when you archive them'}
          </Typography>
        </Box>
      )}

      {/* Enhanced Add Stock Dialog */}
      <AddStockDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAddStock}
      />


      {/* Enhanced Stock Edit Dialog */}
      <EnhancedStockEditDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false })}
        stock={editDialog.entry || null}
        onUpdate={async (updatedStock) => {
          try {
            await loadWatchlist();
            setError(null);
            // Show success message with recommendation prompt
            setSnackbarMessage('Stock updated successfully! Visit the Dashboard to refresh recommendations.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          } catch (error) {
            console.error('Error after stock update:', error);
            setError('Failed to refresh watchlist');
          }
        }}
      />


      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImport={handleBulkImport}
      />

      {/* Portfolio Selection Menu */}
      <Menu
        anchorEl={tradeAnchorEl}
        open={Boolean(tradeAnchorEl)}
        onClose={() => setTradeAnchorEl(null)}
      >
        {portfolios.map((portfolio) => (
          <MenuItem
            key={portfolio.id}
            onClick={() => {
              setSelectedPortfolio(portfolio);
              setTradeExecutionDialog({ 
                open: true, 
                entry: tradeExecutionDialog.entry 
              });
              setTradeAnchorEl(null);
            }}
          >
            {portfolio.name} ({formatCurrency((portfolio as any).availableFundsWithPending || portfolio.availableFunds)} available)
          </MenuItem>
        ))}
      </Menu>

      {/* Trade Execution Dialog */}
      {selectedPortfolio && (
        <TradeExecutionDialog
          open={tradeExecutionDialog.open}
          onClose={() => setTradeExecutionDialog({ open: false, entry: null })}
          watchlistEntry={tradeExecutionDialog.entry}
          portfolioId={selectedPortfolio.id}
          availableFunds={(selectedPortfolio as any).availableFundsWithPending || selectedPortfolio.availableFunds}
          onTradeExecuted={() => {
            loadWatchlist();
            loadPortfolios();
          }}
        />
      )}

      {/* Import Progress */}
      {importProgress.total > 0 && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, bgcolor: 'background.paper', p: 2, borderRadius: 1, boxShadow: 3 }}>
          <Typography variant="body2">
            Importing: {importProgress.current} / {importProgress.total} stocks
          </Typography>
        </Box>
      )}

      {/* Stock Chart Dialog */}
      <StockChartDialog
        chartDialog={chartDialog}
        onClose={() => setChartDialog({ ...chartDialog, open: false })}
      />

      {/* Inactivate Stock Dialog */}
      <Dialog 
        open={inactivateDialog.open} 
        onClose={() => setInactivateDialog({ open: false, entry: null })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Make Stock Inactive</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to make <strong>{inactivateDialog.entry?.symbol}</strong> inactive?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Inactive stocks will not be considered in future Recommendations, but can still exist as positions of any status in Portfolios.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInactivateDialog({ open: false, entry: null })}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={async () => {
              if (inactivateDialog.entry) {
                try {
                  await StockService.setStockInactive(inactivateDialog.entry.symbol);
                  await loadWatchlist(activeTab === 'inactive');
                  setInactivateDialog({ open: false, entry: null });
                } catch (error) {
                  console.error('Error making stock inactive:', error);
                  setError('Failed to make stock inactive');
                }
              }
            }}
          >
            Make Inactive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Stock Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmDialog.open} 
        onClose={() => setDeleteConfirmDialog({ open: false, entry: null })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Delete Stock Permanently</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to permanently delete <strong>{deleteConfirmDialog.entry?.symbol}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. The stock and its historical data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, entry: null })}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={async () => {
              if (deleteConfirmDialog.entry) {
                try {
                  await StockService.deleteInactiveStock(deleteConfirmDialog.entry.symbol);
                  await loadWatchlist(true);
                  setDeleteConfirmDialog({ open: false, entry: null });
                } catch (error: any) {
                  console.error('Error deleting stock:', error);
                  setError(error.message || 'Failed to delete stock');
                }
              }
            }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          action={
            snackbarSeverity === 'success' && snackbarMessage.includes('recommendations') ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  window.location.href = '/recommendations';
                }}
              >
                Go to Recommendations
              </Button>
            ) : undefined
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface StockChartDialogProps {
  chartDialog: {
    open: boolean;
    symbol: string | null;
    pivotPrice?: number;
    currentPrice: number;
    priceChangePercent: number;
    lastUpdated?: Date;
    currentVolume?: number;
    volumeChgVs50Day?: number;
    volumeSpikeRatio?: number;
    tradingDayProgress?: number;
    dataDelay?: number;
  };
  onClose: () => void;
}

function StockChartDialog({ chartDialog, onClose }: StockChartDialogProps) {
  const { data, loading, error, pivotPrice, currentVolume, volumeChgVs50Day } = useStockHistory(chartDialog.symbol || '', 30);

  return (
    <Dialog
      open={chartDialog.open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Historical Price Chart</Typography>
          <IconButton
            onClick={onClose}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {!loading && !error && chartDialog.symbol && (
          <StockChart
            symbol={chartDialog.symbol}
            data={data}
            pivotPrice={pivotPrice || chartDialog.pivotPrice}
            currentPrice={chartDialog.currentPrice}
            priceChangePercent={chartDialog.priceChangePercent}
            lastUpdated={chartDialog.lastUpdated}
            currentVolume={currentVolume || chartDialog.currentVolume}
            volumeChgVs50Day={volumeChgVs50Day ?? chartDialog.volumeChgVs50Day}
            volumeSpikeRatio={chartDialog.volumeSpikeRatio}
            tradingDayProgress={chartDialog.tradingDayProgress}
            dataDelay={chartDialog.dataDelay}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default Watchlist;